import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// Map plan name from DB to tier id
const nameToTier = (name) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('team')) return 'team';
  if (lower.includes('pro')) return 'pro';
  if (lower.includes('solo')) return 'solo';
  if (lower.includes('free')) return 'free';
  return lower;
};

// Parse description JSON from DB
const parseDescription = (desc) => {
  if (!desc) return { description: '', offer: '', feature: [] };
  try {
    const parsed = JSON.parse(desc);
    if (Array.isArray(parsed)) return { description: '', offer: '', feature: parsed };
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        description: parsed.description || parsed.tagline || '',
        offer: parsed.offer || parsed.highlight || '',
        feature: Array.isArray(parsed.feature) ? parsed.feature
          : Array.isArray(parsed.list) ? parsed.list
          : Array.isArray(parsed.features) ? parsed.features
          : [],
      };
    }
  } catch { /* not JSON */ }
  return { description: '', offer: '', feature: [desc] };
};

const SubscriptionPayment = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [dbPlans, setDbPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [confirmStep, setConfirmStep] = useState(null); // null | 'ask' | 'confirm'
  const [pendingPlan, setPendingPlan] = useState(null); // { planId, stripePriceId }
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();

  const [isTeamMember, setIsTeamMember] = useState(false);

  // Fetch current user plan
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: subData } = await supabase
          .from('user_subscriptions')
          .select('start_date')
          .eq('user_id', user.id)
          .single();

        if (!subData || !subData.start_date) {
          setCurrentTier(null);
          return;
        }

        const { data } = await supabase
          .from('user_profiles')
          .select('subscription_tier')
          .eq('id', user.id)
          .single();
        if (data) {
          setCurrentTier(data.subscription_tier);
          if (data.subscription_tier === 'team_member') setIsTeamMember(true);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentPlan();
  }, []);

  // Fetch subscription plans from DB
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true);

        if (error) throw error;
        setDbPlans(data || []);
      } catch (err) {
        console.error('Error fetching plans:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = async (planId, stripePriceId) => {
    if (selecting) return;
    setSelecting(planId);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) throw new Error('Not authenticated');

      // Free plan — cancel existing subscription if any, otherwise just select
      if (planId === 'free' || !stripePriceId) {
        if (currentTier && currentTier !== 'free') {
          // Cancel all active Stripe subscriptions via edge function
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authSession.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              },
            }
          );
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || 'Failed to cancel subscription');
        } else {
          const { error } = await supabase.rpc('select_plan', { p_plan_id: planId });
          if (error) throw error;
        }
        setCurrentTier('free');
        await refreshProfile();
        navigate('/main-dashboard');
        return;
      }

      // Paid plan — create or update Stripe subscription via Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            price_id: stripePriceId,
            success_url: `${window.location.origin}/main-dashboard?payment=success`,
            cancel_url: `${window.location.origin}/subscription-payment?payment=cancelled`,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to process subscription');

      if (result.updated) {
        // Subscription was updated in-place (plan change)
        await refreshProfile();
        navigate('/main-dashboard?payment=success');
      } else {
        // New subscription — redirect to Stripe Checkout.
        // ต่ออายุ session marker ก่อนออกจากแอป (1 ชม.) เผื่อ Safari ITP strip session cookie
        // ระหว่าง cross-site redirect กลับจาก Stripe → กัน AuthContext force-logout เมื่อกลับมา
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        const secureFlag = isHttps ? '; Secure' : '';
        document.cookie = `session_active=1; path=/; SameSite=Lax; Max-Age=3600${secureFlag}`;
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('Plan selection error:', err);
      alert(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSelecting(null);
    }
  };

  // ── Build pricing plans from DB data ──────────────────────────────────────
  const tierOrder = ['free', 'solo', 'pro', 'team'];

  // Group DB plans by tier
  const monthlyPlans = {};
  const annualPlans = {};
  dbPlans.forEach((p) => {
    const tier = nameToTier(p.name);
    if (p.billing_cycle === 'annual') {
      annualPlans[tier] = p;
    } else {
      // monthly or one-time (free)
      monthlyPlans[tier] = p;
    }
  });

  // Calculate max annual savings for badge
  let maxSavings = 0;
  tierOrder.forEach((tier) => {
    const mp = monthlyPlans[tier];
    const ap = annualPlans[tier];
    if (mp && ap) {
      const savings = (mp.price - ap.price) * 12;
      if (savings > maxSavings) maxSavings = savings;
    }
  });

  // Build display plans
  const pricingPlans = tierOrder
    .filter((tier) => monthlyPlans[tier]) // only show tiers that exist in DB
    .map((tier) => {
      const mp = monthlyPlans[tier];
      const ap = annualPlans[tier];
      const parsed = parseDescription(mp.description);

      const isFree = tier === 'free';
      const monthlyPrice = parseFloat(mp.price || 0);
      const credits = mp.max_credits || 0;

      // Annual calculations
      let annualMonthlyPrice = 0;
      let annualTotal = 0;
      let savings = 0;
      if (ap) {
        annualMonthlyPrice = parseFloat(ap.price || 0);
        annualTotal = annualMonthlyPrice * 12;
        savings = (monthlyPrice - annualMonthlyPrice) * 12;
      }

      return {
        id: tier,
        name: mp.name?.replace(/ Monthly$/i, '') || '',
        tagline: parsed.description || '',
        offer: parsed.offer || '',
        features: parsed.feature || [],
        creditsLabel: isFree || !credits ? '' : `${credits.toLocaleString()} credits/month`,
        monthlyPrice,
        annualMonthlyPrice,
        annualTotal,
        savings,
        hasAnnual: !!ap,
        monthlyStripePriceId: mp.stripe_price_id || null,
        annualStripePriceId: ap?.stripe_price_id || null,
        buttonText: isFree ? 'Start for Free' : 'Get Started',
        highlighted: tier === 'pro',
        badge: tier === 'pro' ? 'MOST POPULAR' : tier === 'team' ? 'BEST VALUE' : null,
      };
    });

  return (
    <>
      <Helmet>
        <title>Subscription & Payment - Roomwork.ai</title>
        <meta name="description" content="Choose your subscription plan and unlock professional AI-powered interior design tools" />
      </Helmet>
      <div className="min-h-screen bg-black">
        <Header remainingCredits={0} monthlyLimit={0} />

        <main className="pt-32 pb-16 px-4">
          {isTeamMember && (
            <div className="container mx-auto max-w-2xl mb-8">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertTriangle" size={32} className="text-yellow-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Team Member Account</h2>
                <p className="text-white/60 mb-6">
                  Your account was created and managed by your team owner.<br />
                  You cannot change your subscription plan directly.<br />
                  Please contact your team owner if you need adjustments.
                </p>
                <button
                  onClick={() => navigate('/main-dashboard')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
          <div className="container mx-auto max-w-7xl" style={isTeamMember ? { display: 'none' } : {}}>
            {/* Back Button - Mobile */}
            <button
              onClick={() => navigate('/main-dashboard')}
              className="lg:hidden flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-6"
            >
              <Icon name="ArrowLeft" size={18} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>

            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-white mb-4">Pricing Plans</h1>
              <p className="text-lg text-white/60">Scale your creativity with higher limits, priority access, and the newest features first.</p>
            </div>

            {/* Monthly/Annual Toggle */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'text-white' : 'text-white/40'}`}
              >
                Monthly
              </button>
              <div
                className="w-12 h-6 bg-white/10 rounded-full relative cursor-pointer"
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ease-out ${billingCycle === 'annual' ? 'left-7' : 'left-1'}`} />
              </div>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 text-sm font-medium transition-all ${billingCycle === 'annual' ? 'text-white' : 'text-white/40'}`}
              >
                Annual
              </button>
              {maxSavings > 0 && (
                <span className="ml-2 px-3 py-1 bg-gradient-to-r from-[#F46118] to-[#FE8338] text-black text-xs font-bold rounded-full">
                  Save up to ${maxSavings.toLocaleString()}
                </span>
              )}
            </div>

            {/* Loading */}
            {plansLoading ? (
              <div className="flex items-center justify-center py-20">
                <Icon name="Loader2" size={32} className="animate-spin text-white/40" />
              </div>
            ) : (
              /* Pricing Cards Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative items-stretch">
                {pricingPlans.map((plan) => {
                  const isAnnual = billingCycle === 'annual' && plan.id !== 'free' && plan.hasAnnual;
                  const isCurrent = !loading && currentTier === plan.id;
                  const isSelecting = selecting === plan.id;

                  const displayPrice = isAnnual ? `$${plan.annualMonthlyPrice}` : plan.id === 'free' ? 'Free' : `$${plan.monthlyPrice}`;
                  const originalPrice = isAnnual ? `$${plan.monthlyPrice}` : null;

                  return (
                    <div key={plan.id} className="relative flex flex-col">
                      {/* Badge */}
                      {plan.badge && !isCurrent && (
                        <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                          <span className="glassmorphic-pricing-badge">{plan.badge}</span>
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-[#F46118] to-[#FE8338] text-black">
                            CURRENT PLAN
                          </span>
                        </div>
                      )}

                      <div
                        className={`rounded-3xl p-6 transition-all duration-300 flex flex-col flex-1 ${
                          isCurrent
                            ? 'pricing-card-highlighted ring-2 ring-[#FE8338]'
                            : plan.highlighted
                            ? 'pricing-card-highlighted'
                            : 'pricing-card'
                        }`}
                        style={{
                          background: isCurrent || plan.highlighted
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(20px)',
                          border: isCurrent
                            ? '1px solid rgba(254, 131, 56, 0.4)'
                            : plan.highlighted
                            ? '1px solid rgba(255, 255, 255, 0.15)'
                            : '1px solid rgba(255, 255, 255, 0.08)',
                          boxShadow: isCurrent || plan.highlighted
                            ? '0 8px 32px rgba(254, 131, 56, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                            : '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        {/* Plan Name */}
                        <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                        {/* Tagline / Description */}
                        <p className="text-sm text-white/50 mb-6 min-h-[40px]">{plan.tagline}</p>
                        {/* Price */}
                        <div className="mb-2 min-h-[60px]">
                          {plan.id === 'free' ? (
                            <div className="min-h-[60px]" />
                          ) : isAnnual && originalPrice ? (
                            <>
                              <div className="mb-1">
                                <span className="text-2xl font-bold text-white/40 line-through">{originalPrice}</span>
                              </div>
                              <div>
                                <span className="text-4xl font-bold text-white">{displayPrice}</span>
                                <span className="text-white/50 text-sm ml-1">/mo</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="text-4xl font-bold text-white">{displayPrice}</span>
                              {plan.id !== 'free' && <span className="text-white/50 text-sm ml-1">/mo</span>}
                            </>
                          )}
                        </div>
                        {/* Price Subtext */}
                        <div className="min-h-[48px] mb-4">
                          {plan.id === 'free' ? null : isAnnual && plan.savings > 0 ? (
                            <>
                              <p className="text-sm text-white/60 mb-2">Billed annually</p>
                              <p className="text-sm font-medium text-[#FE8338]">Save ${plan.savings.toLocaleString()} compared to monthly</p>
                            </>
                          ) : (
                            <p className="text-sm text-white/60">Billed monthly</p>
                          )}
                        </div>
                        {/* Credits Badge */}
                        {plan.creditsLabel && (
                          <div className="mb-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-sm text-white/80">
                              <Icon name="Sparkles" size={14} className="text-[#FE8338]" />
                              {plan.creditsLabel}
                            </span>
                          </div>
                        )}
                        {/* Offer */}
                        {plan.offer ? (
                          <p className="text-sm font-medium text-white/50 mb-6 pb-6 border-b border-white/10">
                            {plan.offer}
                          </p>
                        ) : (
                          <div className="mb-6 pb-6 border-b border-white/10" />
                        )}
                        {/* Features */}
                        <ul className="space-y-3 mb-8 flex-grow">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-white/70">
                              <Icon name="Check" size={16} className="text-[#FE8338] mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {/* CTA Button */}
                        <button
                          onClick={() => {
                            if (isCurrent) return;
                            const stripePriceId = isAnnual ? plan.annualStripePriceId : plan.monthlyStripePriceId;
                            // If user already has a non-free plan, show two-step confirmation
                            if (currentTier && currentTier !== 'free') {
                              setPendingPlan({ planId: plan.id, stripePriceId });
                              setConfirmStep('ask');
                              return;
                            }
                            handleSelectPlan(plan.id, stripePriceId);
                          }}
                          disabled={isSelecting || isCurrent}
                          className={`w-full py-3 rounded-2xl font-medium text-sm transition-all duration-300 mt-auto ${
                            isCurrent
                              ? 'bg-white/5 text-white/40 cursor-default'
                              : plan.highlighted
                              ? 'subscription-glassmorphic-button'
                              : 'bg-white/10 text-white hover:bg-white/15 hover:scale-105'
                          }`}
                          style={{
                            boxShadow: !isCurrent && plan.highlighted
                              ? 'inset 0 2px 4px rgba(255, 255, 255, 0.1)'
                              : 'inset 0 2px 4px rgba(255, 255, 255, 0.05)'
                          }}
                        >
                          {isSelecting ? 'Applying...' : isCurrent ? 'Current Plan' : plan.buttonText}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer Note */}
            <div className="text-center mt-12">
              <p className="text-sm text-white/40">
                All paid plans include a commercial license · Credits reset monthly · No rollover
              </p>
              {maxSavings > 0 && (() => {
                const topMonthly = monthlyPlans['team'] || monthlyPlans['pro'] || monthlyPlans['solo'];
                const pct = topMonthly ? Math.round((maxSavings / (topMonthly.price * 12)) * 100) : 20;
                return (
                  <p className="text-sm text-white/60 mt-2 font-medium">
                    Save up to {pct}% with annual billing
                  </p>
                );
              })()}
            </div>
          </div>
        </main>
      </div>

      {/* Two-step confirmation modal */}
      {confirmStep && pendingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className="rounded-2xl p-8 max-w-md w-full mx-4 text-center"
            style={{
              background: 'rgba(30, 30, 30, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
            }}
          >
            {confirmStep === 'ask' ? (
              <>
                <h3 className="text-xl font-bold text-white mb-3">Change Plan</h3>
                <p
                  className={`mb-8 ${pendingPlan?.planId === 'free' ? 'text-red-400' : 'text-white/60'}`}
                >
                  {pendingPlan?.planId === 'free'
                    ? 'Do you want to cancel your subscription?'
                    : 'Do you want to change your current plan?'}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setConfirmStep(null); setPendingPlan(null); }}
                    className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium text-sm hover:bg-white/15 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setConfirmStep('confirm')}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#F46118] to-[#FE8338] text-black font-medium text-sm hover:opacity-90 transition-all"
                  >
                    Yes, Change
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-3">Confirm Change</h3>
                <p className="text-white/60 mb-8">Are you sure you want to proceed?</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setConfirmStep(null); setPendingPlan(null); }}
                    className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium text-sm hover:bg-white/15 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const { planId, stripePriceId } = pendingPlan;
                      setConfirmStep(null);
                      setPendingPlan(null);
                      handleSelectPlan(planId, stripePriceId);
                    }}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#F46118] to-[#FE8338] text-black font-medium text-sm hover:opacity-90 transition-all"
                  >
                    Confirm
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionPayment;
